import React, { useState, useEffect, useRef, useCallback } from 'react';
import s from './NewFaq.module.css';

const FAQ_ITEMS = [
  {
    q: 'כמה זמן לוקח תהליך של היתר בנייה?',
    a: 'בין ארבעה לשמונה חודשים, תלוי ברשות ובמורכבות. אנחנו מנהלים את כל התהליך - מהגרמושקה, דרך פתיחת התיק, תיאום היועצים ועד האישור הסופי. אתם לא מגיעים לוועדה, רק מקבלים את ההיתר.',
  },
  {
    q: 'האם אתם עובדים רק בצפון?',
    a: 'הסטודיו בבאר שבע, אבל עובדים בכל הארץ. מהמדבר ועד המרכז. ביקורי אתר ופיקוח נעשים לפי הצורך.',
  },
  {
    q: 'מה כלול במחיר ומה לא?',
    a: 'ההצעה כוללת תכנון מוקדם עם הדמיות, גרמושקה והיתר, תכניות ביצוע ופיקוח עליון. לא כלולים: יועצים חיצוניים, אגרות עירייה והדפסות - אלה משולמים ישירות.',
  },
  {
    q: 'מה קורה אם משנים דעה באמצע?',
    a: 'שינויים בשלבים מוקדמים - חלק מהתהליך. לכן מציגים שלוש חלופות לפני שמתקדמים. שינוי אחרי אישור רשמי דורש עבודה חוזרת ותשלום נוסף. תמיד נגיד לכם מראש.',
  },
  {
    q: 'איך מתנהלים התשלומים?',
    a: 'ארבע אבני דרך: 25% בפתיחה, 45% עם הגשת ההיתר, 15% בהחלטת הוועדה, 15% במסירת תכניות הביצוע. משלמים לפי התקדמות.',
  },
  {
    q: 'אתם גם מטפלים בביצוע בשטח?',
    a: 'אנחנו לא קבלני ביצוע. בפיקוח העליון מגיעים לאתר ומוודאים שהבנייה נאמנה לתכנית. נוכל להמליץ על קבלנים שאנחנו סומכים עליהם.',
  },
  {
    q: 'אפשר רק עיצוב פנים, בלי אדריכלות?',
    a: 'לגמרי. מקבלים פרויקטים שהבית קיים או שהאדריכלות כבר תוכננה. עיצוב מקיף: קונספט, ריהוט, תאורה, חיפויים, מטבח - עם ליווי ספקים.',
  },
  {
    q: 'כמה פרויקטים מקבלים במקביל?',
    a: 'מעט. כל פרויקט מקבל יחס אישי מלא. מגבילים את המספר כדי לוודא שהזמן והתשומת לב מגיעים לכל לקוח.',
  },
];

export default function NewFaq() {
  const [openIndex, setOpenIndex] = useState(null);
  const sectionRef = useRef(null);
  const itemsRef = useRef([]);
  const ctaRef = useRef(null);

  const toggle = useCallback((i) => {
    setOpenIndex((prev) => (prev === i ? null : i));
  }, []);

  /* Intersection observer for staggered reveal */
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(s.visible);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = itemsRef.current.filter(Boolean);
    items.forEach((el) => observer.observe(el));

    if (ctaRef.current) observer.observe(ctaRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <section className={s.section} ref={sectionRef} id="faq">
      <div className={s.grain} aria-hidden="true" />
      <div className={s.glowA} aria-hidden="true" />

      {/* Header */}
      <header className={s.header}>
        <span className={s.eyebrow}>&mdash; שאלות נפוצות &mdash;</span>
        <h2 className={s.title}>
          כל מה שכדאי לדעת,
          <br />
          <em className={s.titleItalic}>לפני שמתחילים.</em>
        </h2>
        <p className={s.lead}>
          אספנו את השאלות שאנחנו שומעים בפגישה הראשונה. אם משהו עדיין לא ברור - פשוט דברו איתנו.
        </p>
      </header>

      {/* Accordion */}
      <div className={s.accordion}>
        {FAQ_ITEMS.map((item, i) => {
          const isOpen = openIndex === i;
          return (
            <div
              key={i}
              className={`${s.item} ${isOpen ? s.open : ''}`}
              ref={(el) => { itemsRef.current[i] = el; }}
              style={{ '--stagger': `${i * 0.06}s` }}
            >
              <button
                className={s.question}
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${i}`}
                id={`faq-question-${i}`}
              >
                <span className={s.questionText}>{item.q}</span>
                <span className={s.toggleIcon} aria-hidden="true">
                  <span className={s.toggleBar} />
                  <span className={`${s.toggleBar} ${s.toggleBarV}`} />
                </span>
              </button>

              <div
                className={s.answerWrap}
                id={`faq-answer-${i}`}
                role="region"
                aria-labelledby={`faq-question-${i}`}
              >
                <div className={s.answerInner}>
                  <p className={s.answerText}>{item.a}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA Card */}
      <div
        className={s.ctaCard}
        ref={ctaRef}
        style={{ '--stagger': `${FAQ_ITEMS.length * 0.06 + 0.1}s` }}
      >
        <div className={s.ctaGlow} aria-hidden="true" />
        <h3 className={s.ctaTitle}>עדיין נשארה שאלה?</h3>
        <p className={s.ctaText}>
          נשמח לענות אישית בפגישת ייעוץ ראשונה - ללא התחייבות.
        </p>
        <a
          href="https://wa.me/972504499737?text=%D7%94%D7%99%D7%99%2C%20%D7%90%D7%A9%D7%9E%D7%97%20%D7%9C%D7%A7%D7%91%D7%95%D7%A2%20%D7%A4%D7%92%D7%99%D7%A9%D7%AA%20%D7%99%D7%99%D7%A2%D7%95%D7%A5"
          className={s.ctaBtn}
          target="_blank"
          rel="noopener noreferrer"
        >
          קבעו פגישת ייעוץ
        </a>
      </div>
    </section>
  );
}
